import { filter, last, sortBy } from 'lodash';
import {
  Badge,
  Box,
  Divider,
  Heading,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  userStudyYear,
  bacEclaireur,
  userProfileImage,
  userStudyYearSecond,
} from '@inspire/data/helpers/user';
import Panel from '@inspire/ui/general/Panel';
import { GroupEclaireur } from '@inspire/data/static/user';
import HightLights from 'components/utils/HightLights';
import { FinanceStudies } from '@inspire/data/static/static';
import { useEclaireur, useMyProfile } from 'lib/hooks';

const badgeLabels = {
  knewNobodyBacP1: 'Sans repère en Bac+1',
  '!livedWithFamilyBacP1': 'Déménagement',
  studiedAbroad: "Études à l'étranger",
  apprentissage: 'Alternance',
  logementEtudiant: 'Logement étudiant',
  sourceOfFinanceStudies: FinanceStudies,
};

function getEclaireurLastStudy(user: any): any {
  if (!user?.profile?.studies) {
    return null;
  }
  const studyYearsWithoutSkips = filter(
    user.profile.studies,
    (study) => !study.skippingYear
  ).map((studyYear, index) => {
    return {
      ...studyYear,
      ...(user?._highlightResult?.profile?.studies && {
        ...(user?._highlightResult?.profile?.studies[index]?.etablissement
          ?.value && {
          etablissement:
            user?._highlightResult?.profile?.studies[index]?.etablissement
              ?.value,
        }),
        ...(user?._highlightResult?.profile?.studies[index]?.formation
          ?.value && {
          formation:
            user?._highlightResult?.profile?.studies[index]?.formation?.value,
        }),
        ...(user?._highlightResult?.profile?.studies[index]?.ville?.value && {
          ville: user?._highlightResult?.profile?.studies[index]?.ville?.value,
        }),
      }),
    };
  });

  return last(sortBy(studyYearsWithoutSkips, 'year'));
}

interface EclaireurItemProps {
  eclaireur: any;
  group?: GroupEclaireur;
}

const eclaireurLabels = (eclaireur, group) => {
  const {
    myProfile: {
      boostAlternanceApprentissage,
      boostPartir,
      boostConnaisPersonne,
      boostBudget,
    },
  } = useMyProfile();
  const yesOrALittle = [4, 3];

  const badgeLabelsEntries = Object.entries(badgeLabels);
  const priority = [];

  if (group === 'Mobilité') {
    priority.push('!livedWithFamilyBacP1', 'logementEtudiant');
  } else {
    if (yesOrALittle.includes(boostAlternanceApprentissage))
      priority.push('apprentissage');
    if (yesOrALittle.includes(boostPartir))
      priority.push('!livedWithFamilyBacP1', 'logementEtudiant');
    if (yesOrALittle.includes(boostConnaisPersonne))
      priority.push('knewNobodyBacP1');
    if (yesOrALittle.includes(boostBudget))
      priority.push('sourceOfFinanceStudies');
  }

  if (priority.length) {
    badgeLabelsEntries.sort((a, b) => {
      if (priority.includes(a[0]) && priority.includes(b[0])) return 0;
      if (priority.includes(a[0])) return -1;
      if (priority.includes(b[0])) return 1;
      return 0;
    });
  }

  return badgeLabelsEntries.map(([key, value]) => {
    if (typeof value === 'object') {
      return value.map(
        (objectValue) =>
          eclaireur.profile?.[key]?.includes(objectValue.value) && (
            <>
              <Badge>{objectValue.label}</Badge>{' '}
            </>
          )
      );
    }

    const condition =
      key[0] === '!'
        ? !eclaireur.profile[key.substring(1)]
        : !!eclaireur.profile[key];
    return (
      condition && (
        <>
          <Badge>{value}</Badge>{' '}
        </>
      )
    );
  });
};

const EclaireurItem = ({ eclaireur, group }: EclaireurItemProps) => {
  const { firstName } = eclaireur?.profile;
  const isSearchPage = !!eclaireur?._highlightResult?.profile;
  const lastStudy = getEclaireurLastStudy(eclaireur);
  const studyYear = userStudyYear(lastStudy);
  const secondFormation = userStudyYearSecond(lastStudy?.secondFormation);

  if (isSearchPage) {
    const result = useEclaireur(eclaireur._id);
    if (!result.isEclaireurLoading) eclaireur = result.eclaireur;
  }

  return (
    <Box as={Panel} p={0} h="100%" minHeight={group ? 380 : 0}>
      <Box textAlign="center" zIndex={10} p={[2, null, 4]}>
        <SimpleGrid columns={2}>
          <Image
            src={userProfileImage(eclaireur)}
            alt="profile image"
            borderRadius="100px"
            height={['72px', null, '82px']}
            width={['72px', null, '82px']}
            border="2px solid #ededed"
            display="block"
            mx="auto"
          />
          <Box textAlign={'left'}>
            <Heading
              size="md"
              className="all-caps"
              fontFamily="Roboto Condensed"
              mb="0"
            >
              {eclaireur?._highlightResult?.profile?.firstName?.value ? (
                <HightLights
                  value={eclaireur?._highlightResult?.profile?.firstName?.value}
                />
              ) : (
                firstName
              )}
            </Heading>
            <Text m="0" fontSize="sm">
              {bacEclaireur(eclaireur)}
            </Text>
          </Box>
        </SimpleGrid>

        <Heading size="xs" fontFamily="Roboto Condensed">
          {eclaireur?._highlightResult?.profile?.pisteNames ? (
            <>
              {eclaireur?._highlightResult?.profile?.pisteNames?.length > 1 ? (
                <>
                  <VStack>
                    <HightLights
                      value={
                        eclaireur?._highlightResult?.profile?.pisteNames?.[0]
                          .value
                      }
                    />
                    <HightLights
                      value={
                        eclaireur?._highlightResult?.profile?.pisteNames?.[1]
                          .value
                      }
                    />
                  </VStack>
                </>
              ) : (
                <>
                  <VStack>
                    <HightLights
                      value={
                        eclaireur?._highlightResult?.profile?.pisteNames?.[0]
                          .value
                      }
                    />
                    {eclaireur?.profile?.pisteNames?.[1]}{' '}
                  </VStack>
                </>
              )}
            </>
          ) : (
            <>
              <Text>{eclaireur?.pistes?.[0]?.name}</Text>
              <Text>{eclaireur?.pistes?.[1]?.name}</Text>
            </>
          )}
        </Heading>

        {lastStudy && studyYear && (
          <>
            <Divider my={4} />
            <Box fontSize="sm">
              <Text>
                <HightLights
                  value={`<strong>${lastStudy.year}-${
                    lastStudy.year + 1
                  } :</strong> ${studyYear}`}
                />
              </Text>
              {secondFormation && <Text>{secondFormation}</Text>}
            </Box>
          </>
        )}

        <Divider my={4} />
        <Box>{eclaireurLabels(eclaireur, group)}</Box>
      </Box>
    </Box>
  );
};

export default EclaireurItem;
